import React from 'react'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import Icon from '../elementals/icon'
import Text from '../elementals/text'
import InfoPill from '../elementals/infoPill'
import useBreakpoints from '../../hooks/useBreakpoints'
import { ColorNames } from '../../constants/colorVars'
import AiChatContainer, { AiChatContainerProps } from './container'
import AiChatContainerReadOnly from './containerReadOnly'
import type { AiChatContext, AiChatMode } from '.'
import EnhancedDrawer from '../overlays/enhancedDrawer'
import type { primitives } from '@adc/blueprint'
import type { AutomationTarget } from '../../types'

const SparklesIcon = () => (
  <Icon
    name='sparkles'
    iconStyle='regular'
    style={{ fontSize: '1.3em' }}
    color='adc-light-blue'
  />
)

interface AiChatDrawerProps extends AutomationTarget {
  // basic inputs
  context?: AiChatContext
  graphWebsocket: primitives.IGraphWebsocket
  user?: blueprint.primitives.AuctionUser
  taskName?: graph.enums.CognitionEvaluationTaskName

  // mode configuration
  mode?: Extract<AiChatMode, 'chat' | 'mock'> // Only chat and mock modes supported in drawer
  externalMockResponse?: string // Only used when mode is 'mock'

  // external state management
  openChat?: AiChatContainerProps['open']
  setOpenChat: (open: boolean) => void
  externalPrompt?: string
  setExternalPrompt: (prompt: string | undefined) => void
  onExternalPromptProcessed?: AiChatContainerProps['onExternalPromptProcessed']

  // features
  introMessage?: AiChatContainerProps['introMessage']
  hideChatFeedback?: AiChatContainerProps['hideChatFeedback']
  showContext?: AiChatContainerProps['showContext']
  topMarginOnDesktop?: number
  feedbackPromptTextPositive?: AiChatContainerProps['feedbackPromptTextPositive']
  feedbackPromptTextNegative?: AiChatContainerProps['feedbackPromptTextNegative']
  feedbackPromptMoreHelpText?: AiChatContainerProps['feedbackPromptMoreHelpText']
  onSend?: AiChatContainerProps['onSend']
  onAction?: AiChatContainerProps['onAction']
  /** Fires once when a new conversation (evaluation group) is created */
  onConversationStart?: AiChatContainerProps['onConversationStart']

  // reset functionality
  resetKey?: number | string
  onResetComplete?: () => void

  // when true, progress messages are included in the message stream
  showProgress?: boolean

  // visibility filtering — when true, messages with visibility 'PRIVATE' are shown
  showPrivateMessages?: boolean

  // live agent banner
  /** Whether the live agent banner feature is enabled (flag + business hours) */
  enableLiveAgentBanner?: boolean
  /** Callback when live agent banner is clicked  */
  onLiveAgentClick?: () => void
  /** Callback when live agent banner is displayed (for analytics) */
  onLiveAgentBannerDisplayed?: () => void
}

/**
 * Simple wrapper around EnhancedDrawer and AiChatContainer to provide a
 * ready-to-use AI chat drawer component.
 */
const AiChatDrawer = ({
  elmId,
  context,
  taskName = graph.enums.CognitionEvaluationTaskName.BuyerPdpChat,
  mode = 'chat',
  externalMockResponse,
  setOpenChat,
  openChat,
  setExternalPrompt,
  externalPrompt,
  graphWebsocket,
  user = { logged_in: false },
  introMessage,
  hideChatFeedback = true,
  showContext,
  topMarginOnDesktop,
  feedbackPromptTextPositive,
  feedbackPromptTextNegative,
  feedbackPromptMoreHelpText,
  onSend,
  onAction,
  onConversationStart,
  resetKey,
  onResetComplete,
  showProgress = true,
  showPrivateMessages = false,
  enableLiveAgentBanner = false,
  onLiveAgentClick,
  onLiveAgentBannerDisplayed,
}: AiChatDrawerProps) => {
  const breakpoint = useBreakpoints()
  return (
    <EnhancedDrawer
      style={{ zIndex: breakpoint === 'mobile' ? 1100 : 35 }}
      elmId={`${elmId}_drawer`}
      open={openChat || false}
      onClose={() => {
        setOpenChat(false)
      }}
      sidebarWidth={400}
      floatingWidth={600}
      floatingHeight={500}
      topMarginOnDesktop={topMarginOnDesktop}
      headerContent={
        <Text.Header
          level={2}
          style={{
            marginBottom: 0,
            display: 'flex',
            gap: '3px',
            fontWeight: 600,
          }}
        >
          <SparklesIcon /> AI Assistant
          <InfoPill
            foregroundColor={ColorNames.GrayMedium}
            backgroundColor={ColorNames.White}
            content='AI Beta'
            includeBorder
            size='x-small'
            style={{
              marginLeft: '9px',
              marginTop: '4px',
            }}
          />
        </Text.Header>
      }
    >
      {mode === 'chat' ? (
        <AiChatContainer
          elmId={`${elmId}_container`}
          graphWebsocket={graphWebsocket}
          taskName={taskName}
          context={context}
          externalPrompt={externalPrompt}
          onExternalPromptProcessed={() => setExternalPrompt(undefined)}
          showContext={showContext}
          showContextAsLink={false}
          open={openChat || false}
          introMessage={introMessage}
          hideChatFeedback={hideChatFeedback}
          showProgress={showProgress}
          showPrivateMessages={showPrivateMessages}
          feedbackPromptTextPositive={feedbackPromptTextPositive}
          feedbackPromptTextNegative={feedbackPromptTextNegative}
          feedbackPromptMoreHelpText={feedbackPromptMoreHelpText}
          onSend={onSend}
          onAction={onAction}
          onConversationStart={onConversationStart}
          resetKey={resetKey}
          onResetComplete={onResetComplete}
          user={user}
          enableLiveAgentBanner={enableLiveAgentBanner}
          onLiveAgentClick={onLiveAgentClick}
          onLiveAgentBannerDisplayed={onLiveAgentBannerDisplayed}
        />
      ) : (
        <AiChatContainerReadOnly
          elmId={`${elmId}_container`}
          mode={mode}
          context={context}
          externalPrompt={externalPrompt}
          externalMockResponse={externalMockResponse}
          onExternalPromptProcessed={() => setExternalPrompt(undefined)}
          showContext={showContext}
          showContextAsLink={false}
          open={openChat || false}
          introMessage={introMessage}
          hideChatFeedback={hideChatFeedback}
        />
      )}
    </EnhancedDrawer>
  )
}

export default AiChatDrawer
